import Column from '@app/components/layout/Column'
import { useTranslations } from '@i18n/useLanguage'
import { useCallback, useState, useEffect } from 'react'
import { Title } from '@app/components/core/typography/Title'
import { Style } from 'inlines'
import { RadioButtonGroup } from '@app/components/core/radioButton/RadioButtonGroup'
import { PLOT_APPLICATION_STATUSES, PlotApplicationStatus } from 'schema/plot'
import { PlotFilter } from 'functions/app/plot/list/filters'
import { ListRegionModel } from 'functions/app/geography/list/region'
import { Drawer } from '@app/components/blocks/drawer/Drawer'
import { CheckboxGroup } from '@app/components/core/checkbox/CheckboxGroup'
import { Button } from '@app/components/core/button/Button'
import Row from '@app/components/layout/Row'
import useMediaQuery from '@app/util/useMediaQuery'
import { plotApplicationStatusesTranslations } from '@i18n/languages/nl.enumTranslations'
import { useQuery } from '@based/sdk'

const byCrowdbuildingOptions = {
  yes: 'yes',
  no: 'no',
  notSpecified: 'notSpecified',
} as const
type ByCrowdbuildingOption = keyof typeof byCrowdbuildingOptions

export const PlotFilterDrawer = ({
  open,
  onClose,
  setFilters,
  filters,
}: {
  open: boolean
  onClose: VoidFunction
  setFilters: (filters: PlotFilter) => void
  filters: PlotFilter
}) => {
  const { data: regions = [] }: { data?: ListRegionModel[] } =
    useQuery('list-region')
  const isMobile = useMediaQuery()
  const t = useTranslations().app.discoveryPage.filterDrawer
  const [selectedApplicationStatuses, setSelectedApplicationStatuses] =
    useState<PlotApplicationStatus[]>([])
  const [selectedProvinces, setSelectedProvinces] = useState<number[]>([])
  const [byCrowdbuildingOption, setByCrowdbuildingOption] =
    useState<ByCrowdbuildingOption>('notSpecified')

  useEffect(() => {
    if (open) {
      setSelectedApplicationStatuses(filters.applicationStatus ?? [])
      setSelectedProvinces(filters.province ?? [])
      setByCrowdbuildingOption(
        filters.byCrowdbuilding !== null &&
          filters.byCrowdbuilding !== undefined
          ? filters.byCrowdbuilding
            ? 'yes'
            : 'no'
          : 'notSpecified',
      )
    }
  }, [open, filters])

  const toggleSelectedApplicationStatus = (
    applicationStatus: PlotApplicationStatus,
  ) => {
    const remove = selectedApplicationStatuses.includes(applicationStatus)
    if (remove) {
      setSelectedApplicationStatuses((prev) =>
        prev.filter((p) => p !== applicationStatus),
      )
    } else {
      setSelectedApplicationStatuses((prev) => [...prev, applicationStatus])
    }
  }

  const toggleSelectedProvince = (id: number) => {
    const remove = selectedProvinces.includes(id)
    if (remove) {
      setSelectedProvinces((prev) => prev.filter((p) => p !== id))
    } else {
      setSelectedProvinces((prev) => [...prev, id])
    }
  }

  const onSubmit = useCallback(() => {
    const newFilters: PlotFilter = {
      province: selectedProvinces,
      applicationStatus: selectedApplicationStatuses,
    }

    if (byCrowdbuildingOption !== 'notSpecified') {
      newFilters.byCrowdbuilding = byCrowdbuildingOption === 'yes'
    }

    setFilters(newFilters)
    onClose()
  }, [
    selectedProvinces,
    selectedApplicationStatuses,
    byCrowdbuildingOption,
    setFilters,
    onClose,
  ])

  return (
    <Drawer open={open} setOpen={onClose} side="left" title={t.title}>
      <Column
        style={{ height: '100%', flex: 0, justifyContent: 'space-between' }}
      >
        <div>
          <Title.H5>{t.province}</Title.H5>

          <RegionCheckboxGroup
            selected={selectedProvinces}
            onToggle={toggleSelectedProvince}
            regions={regions}
            columns={isMobile ? 1 : 2}
            style={{ marginBottom: '1.5rem' }}
          />

          <Title.H5>{t.applicationStatus}</Title.H5>
          <ApplicationStatusesCheckboxGroup
            selected={selectedApplicationStatuses}
            onToggle={toggleSelectedApplicationStatus}
            columns={isMobile ? 1 : 2}
            style={{ marginBottom: '1.5rem' }}
          />

          <Title.H5>{t.applicationType}</Title.H5>
          <RadioButtonGroup
            labelSize="small"
            style={{
              flexDirection: isMobile ? 'column' : 'row',
              flexWrap: isMobile ? 'nowrap' : 'wrap',
              justifyContent: isMobile ? 'flex-start' : 'space-between',
            }}
            radioButtonStyle={{
              width: isMobile ? '100%' : '13rem',
            }}
            selectedOption={byCrowdbuildingOption}
            options={[
              {
                value: byCrowdbuildingOptions.yes,
                label: t.crowdBuilding,
                tooltipText: t.crowdBuildingTooltip,
              },
              {
                value: byCrowdbuildingOptions.no,
                label: t.external,
                tooltipText: t.externalTooltip,
              },
              {
                value: byCrowdbuildingOptions.notSpecified,
                label: t.noPreference,
              },
            ]}
            onClick={setByCrowdbuildingOption}
          />
        </div>

        <Row
          style={{
            width: '100%',
            justifyContent: isMobile ? 'space-between' : 'flex-end',
          }}
        >
          <Button onClick={onClose} label={t.back} variant="secondary" />
          <Button onClick={onSubmit} label={t.apply} />
        </Row>
      </Column>
    </Drawer>
  )
}

interface FilterCheckboxGroupProps<T> {
  selected: T[]
  onToggle: (value: T) => void
  style?: Style
}

const RegionCheckboxGroup = ({
  selected,
  onToggle,
  regions,
  columns,
  style,
}: FilterCheckboxGroupProps<number> & {
  regions?: ListRegionModel[]
  columns?: number
}) => {
  return (
    <CheckboxGroup
      options={regions?.map((region) => ({
        label: region.name,
        value: region.id,
        key: `${region.id}_region_checkbox`,
      }))}
      columns={columns}
      selected={selected}
      onToggle={onToggle}
      style={style}
    />
  )
}

const ApplicationStatusesCheckboxGroup = ({
  selected,
  onToggle,
  columns,
  style,
}: FilterCheckboxGroupProps<PlotApplicationStatus> & { columns?: number }) => {
  return (
    <CheckboxGroup
      options={Object.values(PLOT_APPLICATION_STATUSES).map((status) => ({
        label: plotApplicationStatusesTranslations[status],
        value: status,
        key: `${status}_application_status_checkbox`,
      }))}
      columns={columns ?? 1}
      selected={selected}
      onToggle={onToggle}
      style={style}
    />
  )
}
